import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, signal } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { environment } from '../../../../environments/environment';

interface SheetPerson { id: string; name: string; daysSinceLastAction: number | null; }
interface GroupSheet {
  id: string; name: string; privacy: string; sportName: string;
  locationName: string | null; createdAt: string | null; description: string | null; hasCover: boolean;
  owner: SheetPerson | null;
  admins: SheetPerson[];
  membersTotal: number; membersActive90: number;
  activitiesTotal: number; upcomingActivities: number;
  joinedSharePct: number | null; lastActivityAt: string | null;
  recentActivities: Array<{ title: string; startDate: string; partners: number }>;
  members: Array<{ id: string; name: string; heat: string }>;
  membersMore: number;
}

/**
 * The living group sheet from the validated mockup: context, the creator and
 * admins with their recency, member heat through the shared rule, the
 * activity signal (partners organiser excluded), and one orange living rule
 * telling the admin what to do about this group right now.
 */
@Component({
  selector: 'bo-group-sheet',
  templateUrl: './group-sheet.component.html',
  styleUrls: ['./group-sheet.component.scss'],
  standalone: true,
  imports: [CommonModule, DatePipe, RouterLink, ButtonModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GroupSheetComponent implements OnChanges {
  @Input({ required: true }) groupId!: string;
  @Output() editRequested = new EventEmitter<void>();
  @Output() engageRequested = new EventEmitter<void>();
  @Output() deleteRequested = new EventEmitter<void>();

  readonly sheet = signal<GroupSheet | null>(null);
  readonly loading = signal(false);
  readonly failed = signal(false);

  constructor(private http: HttpClient, private translate: TranslateService) {}

  ngOnChanges(): void {
    if (!this.groupId) {
      return;
    }
    this.loading.set(true);
    this.failed.set(false);
    this.sheet.set(null);
    this.http
      .get<GroupSheet>(`${environment.api}/api/v1/admin/groups/${this.groupId}/sheet`)
      .subscribe({
        next: (res) => { this.loading.set(false); this.sheet.set(res ?? null); },
        error: () => { this.loading.set(false); this.failed.set(true); },
      });
  }

  daysSince(iso: string | null): number | null {
    if (!iso) return null;
    return Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
  }

  /**
   * The living rule of the sheet, recomputed on every read. One sentence, one
   * tone: alert when the group risks dying, mixed when it dozes, good
   * otherwise. The 88% rule from the mockup, applied group by group.
   */
  signal_(): { tone: 'alert' | 'mixed' | 'good'; text: string } | null {
    const s = this.sheet();
    if (!s) return null;
    const lastDays = this.daysSince(s.lastActivityAt);
    const alive = lastDays !== null && lastDays < 90;
    const t = (key: string, params?: Record<string, unknown>) => this.translate.instant(key, params);

    if (s.admins.length === 0 && alive) {
      return {
        tone: 'alert',
        text: t('groupSheet.signalAliveNoAdmin', {
          owner: s.owner?.name ?? '-',
          days: s.owner?.daysSinceLastAction ?? '?',
        }),
      };
    }
    if (s.admins.length === 0 && s.activitiesTotal === 0) {
      return { tone: 'alert', text: t('groupSheet.signalEmptyNoAdmin') };
    }
    if (s.admins.length === 0) {
      return { tone: 'alert', text: t('groupSheet.signalNoAdmin') };
    }
    if (lastDays === null || lastDays >= 180) {
      return { tone: 'mixed', text: t('groupSheet.signalDozing', { days: lastDays ?? '—' }) };
    }
    return { tone: 'good', text: t('groupSheet.signalHealthy') };
  }
}
